import React, { useState } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Box,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    Alert,
    Snackbar,
} from "@mui/material";
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Logout as LogoutIcon,
    Person as PersonIcon,
    People as PeopleIcon,
    VideoLibrary as VideoLibraryIcon,
    Cached as CachedIcon,
    FeedbackOutlined as FeedbackOutlinedIcon,
    DriveFileRenameOutline as DriveFileRenameOutlineIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import { authStore } from "../stores";
import Dashboard from "./Dashboard";
import SiteFormsPage from "./SiteFormsPage";
import ClientsManagement from "./ClientsManagement";
import MediaVisibilityManagement from "./MediaVisibilityManagement";
import MediaNameManagement from "./MediaNameManagement";
import UsersManagement from "./UsersManagement";
import { apiService } from "../services/api";

const drawerWidth = 240;

const AppLayout: React.FC = observer(() => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [currentPage, setCurrentPage] = useState<
        "dashboard" | "site-forms" | "clients" | "media" | "media-names" | "users"
    >("dashboard");
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
        open: false,
        message: "",
        severity: "success",
    });

    const userRole = authStore.user?.role || "supplier";
    const isAdmin = userRole === "admin";
    const isClient = userRole === "client";

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        authStore.logout();
        handleMenuClose();
    };

    const handleClearCache = async () => {
        try {
            await apiService.clearCache();
            setSnackbar({
                open: true,
                message: "Кэш успешно очищен",
                severity: "success",
            });
            handleMenuClose();
        } catch (error) {
            console.error("Ошибка очистки кэша:", error);
            setSnackbar({
                open: true,
                message: "Ошибка очистки кэша",
                severity: "error",
            });
            handleMenuClose();
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const drawer = (
        <div>
            <Toolbar>
                <Typography variant="h6" noWrap component="div">
                    LK-Lite
                </Typography>
            </Toolbar>
            <Divider />
            <List>
                <ListItemButton
                    selected={currentPage === "dashboard"}
                    onClick={() => setCurrentPage("dashboard")}
                    sx={{
                        backgroundColor: currentPage === "dashboard" ? "rgba(0, 188, 212, 0.1)" : "transparent",
                        "&:hover": {
                            backgroundColor: "rgba(0, 188, 212, 0.2)",
                        },
                    }}
                >
                    <ListItemIcon>
                        <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Дашборд" />
                </ListItemButton>

                <ListItemButton
                    selected={currentPage === "site-forms"}
                    onClick={() => setCurrentPage("site-forms")}
                    sx={{
                        backgroundColor: currentPage === "site-forms" ? "rgba(0, 188, 212, 0.1)" : "transparent",
                        "&:hover": {
                            backgroundColor: "rgba(0, 188, 212, 0.2)",
                        },
                    }}
                >
                    <ListItemIcon>
                        <FeedbackOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText primary="Формы обратной связи" />
                </ListItemButton>

                {isAdmin && (
                    <ListItemButton
                        selected={currentPage === "clients"}
                        onClick={() => setCurrentPage("clients")}
                        sx={{
                            backgroundColor: currentPage === "clients" ? "rgba(0, 188, 212, 0.1)" : "transparent",
                            "&:hover": {
                                backgroundColor: "rgba(0, 188, 212, 0.2)",
                            },
                        }}
                    >
                        <ListItemIcon>
                            <PeopleIcon />
                        </ListItemIcon>
                        <ListItemText primary="Управление клиентами" />
                    </ListItemButton>
                )}
                {isAdmin && (
                    <ListItemButton
                        selected={currentPage === "media"}
                        onClick={() => setCurrentPage("media")}
                        sx={{
                            backgroundColor: currentPage === "media" ? "rgba(0, 188, 212, 0.1)" : "transparent",
                            "&:hover": {
                                backgroundColor: "rgba(0, 188, 212, 0.2)",
                            },
                        }}
                    >
                        <ListItemIcon>
                            <VideoLibraryIcon />
                        </ListItemIcon>
                        <ListItemText primary="Управление медиа" />
                    </ListItemButton>
                )}
                {isAdmin && (
                    <ListItemButton
                        selected={currentPage === "media-names"}
                        onClick={() => setCurrentPage("media-names")}
                        sx={{
                            backgroundColor: currentPage === "media-names" ? "rgba(0, 188, 212, 0.1)" : "transparent",
                            "&:hover": {
                                backgroundColor: "rgba(0, 188, 212, 0.2)",
                            },
                        }}
                    >
                        <ListItemIcon>
                            <DriveFileRenameOutlineIcon />
                        </ListItemIcon>
                        <ListItemText primary="Названия медиа" />
                    </ListItemButton>
                )}
                {isAdmin && (
                    <ListItemButton
                        selected={currentPage === "users"}
                        onClick={() => setCurrentPage("users")}
                        sx={{
                            backgroundColor: currentPage === "users" ? "rgba(0, 188, 212, 0.1)" : "transparent",
                            "&:hover": {
                                backgroundColor: "rgba(0, 188, 212, 0.2)",
                            },
                        }}
                    >
                        <ListItemIcon>
                            <PersonIcon />
                        </ListItemIcon>
                        <ListItemText primary="Управление пользователями" />
                    </ListItemButton>
                )}
            </List>
        </div>
    );

    return (
        <Box sx={{ display: "flex" }}>
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: "none" } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        {isClient ? "Статистика клиента" : "Статистика поставщика"}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">{authStore.user?.login}</Typography>
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenuOpen}
                            color="inherit"
                        >
                            <Avatar sx={{ width: 32, height: 32 }}>
                                <PersonIcon />
                            </Avatar>
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            {isAdmin && (
                                <MenuItem onClick={handleClearCache}>
                                    <ListItemIcon>
                                        <CachedIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Очистить кэш" />
                                </MenuItem>
                            )}
                            {isAdmin && <Divider />}
                            <MenuItem onClick={handleLogout}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Выйти" />
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: "block", sm: "none" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    minHeight: "100vh",
                    backgroundColor: "background.default",
                }}
            >
                <Toolbar />
                {currentPage === "dashboard" && <Dashboard />}
                {currentPage === "site-forms" && <SiteFormsPage />}
                {currentPage === "clients" && <ClientsManagement />}
                {currentPage === "media" && <MediaVisibilityManagement />}
                {currentPage === "media-names" && <MediaNameManagement />}
                {currentPage === "users" && <UsersManagement />}
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
});

export default AppLayout;
